/**
 * FAQ 2025 Component JavaScript
 * Handles accordion functionality with single-open behavior per group
 */

class FAQ2025 {
  constructor() {
    this.groups = document.querySelectorAll('[data-faq-group]');
    this.init();
  }

  init() {
    if (this.groups.length === 0) return;

    this.groups.forEach(group => {
      this.setupGroup(group);
    });

    // Handle browser back/forward navigation
    window.addEventListener('popstate', () => {
      this.handleUrlState();
    });

    // Check for URL hash on load
    this.handleUrlState();
  }

  setupGroup(group) {
    const items = group.querySelectorAll('[data-faq-item]');

    items.forEach(item => {
      const question = item.querySelector('[data-faq-question]');

      if (question) {
        // Add click event listener
        question.addEventListener('click', (e) => {
          this.handleQuestionClick(e, item, group);
        });

        // Add keyboard event listener for accessibility
        question.addEventListener('keydown', (e) => {
          this.handleKeydown(e, item, group);
        });

        // Ensure proper ARIA attributes
        this.setAriaAttributes(item);
      }
    });
  }

  handleQuestionClick(e, clickedItem, group) {
    e.preventDefault();

    const isCurrentlyOpen = clickedItem.hasAttribute('open');

    // Close all other items in this group
    this.closeAllItemsInGroup(group);

    // Toggle the clicked item
    if (!isCurrentlyOpen) {
      this.openItem(clickedItem);
      this.updateUrl(clickedItem);
    } else {
      this.closeItem(clickedItem);
      this.clearUrl();
    }
  }

  handleKeydown(e, item, group) {
    // Handle Enter and Space keys for accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleQuestionClick(e, item, group);
    }
  }

  closeAllItemsInGroup(group) {
    const items = group.querySelectorAll('[data-faq-item]');
    items.forEach(item => {
      this.closeItem(item);
    });
  }

  openItem(item) {
    item.setAttribute('open', '');
    this.updateAriaAttributes(item, true);

    // Smooth scroll to item if it's not in viewport
    this.scrollToItemIfNeeded(item);
  }

  closeItem(item) {
    item.removeAttribute('open');
    this.updateAriaAttributes(item, false);
  }

  setAriaAttributes(item) {
    const question = item.querySelector('[data-faq-question]');
    const answer = item.querySelector('.faq-2025__answer');

    if (question && answer) {
      const questionId = this.generateId('faq-question');
      const answerId = this.generateId('faq-answer');

      question.setAttribute('aria-expanded', 'false');
      question.setAttribute('aria-controls', answerId);
      question.setAttribute('id', questionId);

      answer.setAttribute('aria-labelledby', questionId);
      answer.setAttribute('id', answerId);
      answer.setAttribute('role', 'region');
    }
  }

  updateAriaAttributes(item, isOpen) {
    const question = item.querySelector('[data-faq-question]');
    if (question) {
      question.setAttribute('aria-expanded', isOpen.toString());
    }
  }

  generateId(prefix) {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }

  scrollToItemIfNeeded(item) {
    const itemRect = item.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Check if item is not fully visible
    if (itemRect.top < 0 || itemRect.bottom > viewportHeight) {
      setTimeout(() => {
        item.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100); // Small delay to allow for opening animation
    }
  }

  updateUrl(item) {
    const question = item.querySelector('[data-faq-question]');
    if (question) {
      const questionText = question.querySelector('.faq-2025__question-text').textContent;
      const slug = this.createSlug(questionText);

      if (history.pushState) {
        const newUrl = `${window.location.pathname}${window.location.search}#${slug}`;
        history.pushState(null, '', newUrl);
      }
    }
  }

  clearUrl() {
    if (history.pushState) {
      const newUrl = `${window.location.pathname}${window.location.search}`;
      history.pushState(null, '', newUrl);
    }
  }

  createSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .trim()
      .replace(/[\s-]+/g, '-') // Replace spaces and multiple hyphens with single hyphen
      .substring(0, 50); // Limit length
  }

  handleUrlState() {
    const hash = window.location.hash.substring(1);

    if (!hash) return;

    // Find matching question by slug
    const allQuestions = document.querySelectorAll('[data-faq-question]');

    for (const question of allQuestions) {
      const questionText = question.querySelector('.faq-2025__question-text').textContent;
      const questionSlug = this.createSlug(questionText);

      if (questionSlug === hash) {
        const item = question.closest('[data-faq-item]');
        const group = question.closest('[data-faq-group]');

        if (item && group) {
          // Close all items in group first
          this.closeAllItemsInGroup(group);
          // Then open the matched item
          this.openItem(item);
        }
        break;
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FAQ2025();
  });
} else {
  new FAQ2025();
}

// Export for potential external use
window.FAQ2025 = FAQ2025;
